// Spec 078 FR-006 — excluir modelo: lista as máquinas afetadas (voltam ao bloco genérico) e exige
// digitar o nome (regra do design system para ação irreversível). Input controlado; o primário só
// habilita quando o texto é exatamente o nome.
import * as React from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ModalOverlay } from "@/components/ui/modal-overlay";
import { Notify, toast } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { useDeleteModel3d, useModel3d } from "../hooks";
import type { Model3dSummary } from "@/types/models-3d.types";

export interface DeleteModelDialogProps {
  model: Model3dSummary;
  onClose: () => void;
}

export function DeleteModelDialog({ model, onClose }: DeleteModelDialogProps): React.ReactElement {
  const detail = useModel3d(model.id);
  const remove = useDeleteModel3d();
  const [typed, setTyped] = React.useState("");
  const ready = typed === model.name;
  const machines = detail.data?.machines ?? [];

  async function confirm() {
    try {
      await remove.mutateAsync({ id: model.id, confirmationName: typed });
      toast.success(`Modelo "${model.name}" excluído.`, {
        description:
          machines.length > 0
            ? `${machines.length} máquina(s) voltaram ao bloco genérico.`
            : undefined,
      });
      onClose();
    } catch (err) {
      toast.error("Falha ao excluir.", { description: getFriendlyErrorMessage(err) });
    }
  }

  return (
    <ModalOverlay onClose={onClose} title={`Excluir modelo ${model.name}`} className="max-w-lg">
      <div className="flex flex-col gap-4 p-5">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">Excluir “{model.name}”?</h2>
          <p className="text-sm text-text-secondary">
            O arquivo e a calibração deixam de existir. Esta ação não pode ser desfeita.
          </p>
        </div>
        {detail.isError ? (
          <Notify.QueryError
            error={detail.error}
            variant="inline"
            onRetry={() => void detail.refetch()}
          />
        ) : null}
        {machines.length > 0 ? (
          <Notify.Banner
            variant="warning"
            title={`${machines.length} máquina(s) usam este modelo e passarão ao bloco genérico:`}
            description={machines.map((m) => `${m.name} (${m.lineName})`).join(" · ")}
          />
        ) : detail.data ? (
          <p className="text-sm text-text-tertiary">Nenhuma máquina usa este modelo.</p>
        ) : null}
        <div className="space-y-1.5">
          <Label htmlFor="m3d-delete-name">Para confirmar, digite {model.name}</Label>
          <Input
            id="m3d-delete-name"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="ghost" onClick={onClose} disabled={remove.isPending}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={() => void confirm()}
            disabled={!ready || remove.isPending}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            {remove.isPending ? "Excluindo…" : "Excluir modelo"}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
